<template>
  <div class="activity-log-view">
    <!-- Back header -->
    <div class="view-header">
      <button class="back-btn" @click="$emit('back')">
        <i class="tim-icons icon-minimal-left"></i>
      </button>
      <h5 class="view-title mb-0">Activity log</h5>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="view-body text-center py-5">
      <i class="el-icon-loading" style="font-size: 24px;"></i>
    </div>

    <!-- Empty state -->
    <div v-else-if="activityLog.length === 0" class="view-body">
      <div class="empty-state">
        <i class="tim-icons icon-bullet-list-67 text-muted mb-2" style="font-size: 24px; display: block;"></i>
        <p class="text-muted small mb-0">No events have been recorded</p>
      </div>
    </div>

    <!-- Activity log list -->
    <div v-else class="view-body">
      <div class="log-list">
        <div v-for="(item, index) in activityLog" :key="'log-' + index" class="log-item">
          <div class="log-row">
            <div class="log-left">
              <div class="log-event text-white">
                {{ item.description }}{{ isOneSided(item) ? ':' : '' }}
                <span v-if="item.new_value && item.old_value && isTime(item.new_value)">
                  from {{ formatTime(item.old_value) }} to {{ formatTime(item.new_value) }}
                </span>
                <span v-else-if="item.new_value && item.old_value && isDate(item.new_value)">
                  from {{ formatDate(item.old_value) }} to {{ formatDate(item.new_value) }}
                </span>
                <span v-else-if="item.new_value && item.old_value">
                  from {{ item.old_value }} to {{ item.new_value }}
                </span>
                <span v-else-if="item.new_value && !item.old_value" class="log-detail">
                  {{ item.new_value }}
                </span>
                <span v-else-if="item.old_value && !item.new_value" class="log-detail">
                  {{ item.old_value }}
                </span>
              </div>
              <div class="log-actor text-muted small">
                <template v-if="!item.end_user_id && !item.company_user_id">System</template>
                <template v-else-if="item.end_user_id && !item.company_user_id">{{ item.end_user_name }}</template>
                <template v-else-if="!item.end_user_id && item.company_user_id">{{ item.company_user_name }}</template>
                <span v-if="item.party_label" class="log-party">{{ item.party_label }}</span>
              </div>
            </div>
            <div class="log-date text-muted small">
              {{ getLocalDate(item.timestamp) }}, {{ getLocalTime(item.timestamp) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { formatGroupDate, formatGroupTime } from '@/plugins/dates';

export default {
  name: 'ActivityLogView',

  props: {
    activityLog: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    dateFormat: { type: String, default: 'MM-DD-YYYY' },
    timeFormat: { type: String, default: '12h' },
    showDayOfWeek: { type: Boolean, default: false }
  },

  methods: {
    isOneSided(item) {
      return Boolean((item.new_value && !item.old_value) || (item.old_value && !item.new_value));
    },

    isTime(str) {
      return /^\d{2}:\d{2}(:\d{2})?$/.test(str);
    },

    isDate(str) {
      return moment(str, 'YYYY-MM-DD', true).isValid();
    },

    formatTime(time) {
      if (!time) return '';
      const t = time.includes('T') ? time.split('T')[1] : time;
      const fmt = this.timeFormat === 'HH:mm' ? 'HH:mm' : 'h:mm A';
      return moment(t, 'HH:mm:ss').format(fmt);
    },

    formatDate(d) {
      if (!d) return '';
      const dateStr = d.length > 10 ? d.split('T')[0] : d;
      return moment(dateStr).format(this.dateFormat);
    },

    getLocalDate(d) {
      return formatGroupDate(d);
    },

    getLocalTime(d) {
      return formatGroupTime(d);
    }
  }
};
</script>

<style lang="scss" scoped>
.view-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.back-btn {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
}

.view-title {
  font-size: 14px;
  font-weight: 600;
  color: white;
}

.view-body {
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
}

.log-list {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.log-item {
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);

  &:last-child {
    border-bottom: none;
  }
}

.log-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.log-left {
  flex: 1;
  min-width: 0;
}

.log-event {
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 2px;
}

.log-detail {
  color: rgba(255, 255, 255, 0.5);
}

.log-actor {
  font-weight: 500;
}

/* Party the row belongs to on a public slot (rows written against a child booking) */
.log-party {
  color: #e14eca;
  &::before { content: '\00b7'; margin: 0 6px; color: rgba(125, 125, 125, 0.8); }
}

.log-date {
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
  line-height: 1.4;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
}
</style>

<style lang="scss">
.white-content {
  .activity-log-view .view-header {
    border-bottom-color: #e3e3e3;
  }

  .activity-log-view .back-btn {
    background: #f5f6fa;
    border-color: #e3e3e3;
    color: #9A9A9A;

    &:hover {
      background: #e9ecef;
      color: #344675;
    }
  }

  .activity-log-view .view-title {
    color: #344675;
  }

  .activity-log-view .log-event {
    color: #344675;
  }

  .activity-log-view .log-item {
    border-bottom-color: #e3e3e3;
  }
}
</style>
